import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  NUM_SECTIONS,
  PRACTICE_TYPE_DATA,
  PracticeTestType,
  SECTION_ID_TO_PRACTICE_TEST_TYPE,
  SECTION_TO_SUBSECTION_IDS,
  TEST_TYPE_TO_RESULT_TITLE,
} from "../../appsConfig";
import { useVisibleTests } from "@/hooks/useVisibleTests";
import { getCurrentLanguage } from "@/i18n";
import {
  getLocalizedPracticeTypeResultTitle,
  localizePracticeTypeData,
} from "@/i18n/practiceTypeCopy";
import { localizeSubsectionName } from "@/i18n/serverContentCopy";
import { useRootStore } from "@/state/RootStore";
import type { Test } from "@/state/TestsStore";
import type {
  ResultsAttempt,
  ResultsDashboardModel,
  ResultsSubtopic,
  ResultsTopic,
} from "@/types/results";
import { resolveStateAwareNumber } from "@/utils/curriculum";
import { formatDayMonth, formatLong, formatTime } from "@/utils/date";
import { splitTrendWindows } from "@/utils/trendWindows";

interface ScoreTotal {
  correct: number;
  total: number;
}

const toPercent = ({ correct, total }: ScoreTotal) =>
  total > 0 ? Math.round((correct / total) * 100) : 0;

const aggregateScore = (tests: Test[], sectionId?: number): ScoreTotal =>
  tests.reduce(
    (result, test) => ({
      correct: result.correct + test.numCorrect(sectionId),
      total: result.total + test.numQuestions(sectionId),
    }),
    { correct: 0, total: 0 },
  );

const averageAnswerTime = (tests: Test[]): number | null => {
  const totals = tests.reduce(
    (result, test) => {
      if (test.averageAnswerTime == null || test.numAnswered <= 0) return result;
      return {
        answered: result.answered + test.numAnswered,
        seconds: result.seconds + test.averageAnswerTime * test.numAnswered,
      };
    },
    { answered: 0, seconds: 0 },
  );

  return totals.answered > 0 ? totals.seconds / totals.answered : null;
};

const getDifference = (current: number, previous: number | null) =>
  previous == null ? null : current - previous;

const getTestTitle = (test: Test) => {
  const data = localizePracticeTypeData(
    test.practiceTestType,
    PRACTICE_TYPE_DATA[test.practiceTestType] || {},
  );
  return data.resultsTitle || data.title || "Practice";
};

const buildAttempts = (tests: Test[], locale: string): ResultsAttempt[] =>
  tests.map((test, index) => {
    const previous = tests[index + 1];
    const score = Math.round(test.percentCorrect() * 100);
    const answerTimeSeconds = test.averageAnswerTime ?? null;
    const previousAnswerTime = previous?.averageAnswerTime ?? null;

    return {
      answerTimeSeconds,
      completedAt: (test.endTime || test.startTime).toDate(),
      dateLabel: formatLong(test.endTime || test.startTime, locale),
      id: test.id,
      image: PRACTICE_TYPE_DATA[test.practiceTestType]?.image,
      questionCount: test.numQuestions(),
      score,
      scoreChange: previous
        ? score - Math.round(previous.percentCorrect() * 100)
        : null,
      speedChangeSeconds:
        answerTimeSeconds != null && previousAnswerTime != null
          ? Math.round(answerTimeSeconds - previousAnswerTime)
          : null,
      timeLabel: formatTime(test.endTime || test.startTime, locale),
      title: getTestTitle(test),
    };
  });

export function useResultsDashboard(): {
  allTests: Test[];
  model: ResultsDashboardModel | null;
} {
  const store = useRootStore();
  const visibleTests = useVisibleTests();
  const { t, i18n } = useTranslation();
  const locale = i18n.language || getCurrentLanguage();
  const stateCode = store.mainAppStore.selectedStateCode || "";

  return useMemo(() => {
    const allTests = Array.from(visibleTests.values())
      .filter((test) => test.isFinished && test.allQuestionsLoaded())
      .sort((a, b) => b.startTime.unix() - a.startTime.unix());

    if (allTests.length === 0) return { allTests, model: null };

    const { recent, prior } = splitTrendWindows(allTests);
    const overall = aggregateScore(recent);
    const priorOverall = aggregateScore(prior);
    const overallScore = toPercent(overall);
    const priorScore = priorOverall.total > 0 ? toPercent(priorOverall) : null;
    const currentTime = averageAnswerTime(recent);
    const priorTime = averageAnswerTime(prior);
    const rawWeights = Array.from({ length: NUM_SECTIONS }, (_, index) => {
      const raw = store.questionsStore.curriculum[String(index + 1)]?.per_of_q;
      return PracticeTestType.byState
        ? resolveStateAwareNumber(raw, stateCode, 0)
        : resolveStateAwareNumber(raw, undefined, 0);
    });
    const totalWeight = rawWeights.reduce((sum, value) => sum + value, 0) || 1;

    const topics = Array.from({ length: NUM_SECTIONS }, (_, index) => {
      const sectionId = index + 1;
      const sectionScore = aggregateScore(recent, sectionId);
      if (sectionScore.total === 0) return null;

      const priorSectionScore = aggregateScore(prior, sectionId);
      const practiceType = SECTION_ID_TO_PRACTICE_TEST_TYPE[sectionId];
      const subsectionIds = SECTION_TO_SUBSECTION_IDS[sectionId] || [];
      const subtopics: ResultsSubtopic[] = subsectionIds.flatMap((subsectionId) => {
        const subsectionScore = recent.reduce(
          (result, test) => ({
            correct: result.correct + test.numCorrectSubsection(subsectionId),
            total: result.total + test.numQuestionsSubsection(subsectionId),
          }),
          { correct: 0, total: 0 },
        );
        if (subsectionScore.total === 0) return [];

        return [{
          correct: subsectionScore.correct,
          id: subsectionId,
          score: toPercent(subsectionScore),
          title:
            localizeSubsectionName(
              store.testStore.subsectionNames.get(String(subsectionId)),
            ) || t("results.subcategory_fallback", { id: subsectionId }),
          total: subsectionScore.total,
        }];
      });

      const topic: ResultsTopic = {
        correct: sectionScore.correct,
        examWeight: Math.round((rawWeights[index] / totalWeight) * 100),
        improvement:
          priorSectionScore.total > 0
            ? toPercent(sectionScore) - toPercent(priorSectionScore)
            : null,
        score: toPercent(sectionScore),
        sectionId,
        subtopics,
        title:
          getLocalizedPracticeTypeResultTitle(
            practiceType,
            TEST_TYPE_TO_RESULT_TITLE[practiceType],
          ) || t("results.section_fallback", { id: sectionId }),
        total: sectionScore.total,
      };
      return topic;
    }).filter((topic): topic is ResultsTopic => topic !== null);

    const attempts = buildAttempts(allTests, locale);
    const comparisonDate = prior.at(-1)?.startTime || prior[0]?.startTime;
    return {
      allTests,
      model: {
        attempts,
        averageAnswerTimeSeconds: currentTime,
        averageTimeChangeSeconds:
          currentTime != null && priorTime != null
            ? Math.round(currentTime - priorTime)
            : null,
        comparisonDateLabel: comparisonDate
          ? formatDayMonth(comparisonDate, locale)
          : null,
        overallScore,
        overallScoreChange: getDifference(overallScore, priorScore),
        passingScore: Math.round(
          store.questionsStore.getPassingScore(stateCode) * 100,
        ),
        recentAttempt: attempts[0],
        topics,
      },
    };
  }, [locale, stateCode, store, t, visibleTests]);
}
